import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

type Theme = "Black" | "Light" | string

type ThemeContextType = {
  theme: Theme
  setTheme: React.Dispatch<React.SetStateAction<Theme>>
  isPortrait: boolean
}

export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined
)

type Props = {
  children: ReactNode
}

export function ThemeProvider({ children }: Props) {
  const [theme, setTheme] = useState<Theme>("Black")
  const [isPortrait, setIsPortrait] = useState<boolean>(true)

  // INIT SAFE (CLIENT ONLY)
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme")
    if (savedTheme) setTheme(savedTheme)

    const mq = window.matchMedia("(orientation: portrait)")
    setIsPortrait(mq.matches)
  }, [])

  // ORIENTATION LISTENER
  useEffect(() => {
    const mq = window.matchMedia("(orientation: portrait)")

    const handleChange = () => {
      const portrait = mq.matches
      setIsPortrait(portrait)

      // UX rule: force Black in landscape
      if (!portrait) setTheme("Black")
    }
    mq.addEventListener("change", handleChange)

    return () => mq.removeEventListener("change", handleChange)
  }, [])

  // APPLY THEME SIDE EFFECT
  useEffect(() => {
    if (!theme) return

    document.body.dataset.theme = theme
    localStorage.setItem("theme", theme)
  }, [theme])

  
  // MEMO CONTEXT VALUE
  const value = useMemo(
    () => ({
      theme,
      setTheme,
      isPortrait,
    }),
    [theme, isPortrait]
  )

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}


// SAFE HOOK (RECOMENDADO)
export const useTheme = () => {
  const ctx = createContext(ThemeContext)
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider")
  return ctx
}